import React, { useState, useCallback, useMemo } from "react";
import { Table, Button, Col, Row, Tooltip } from "antd";
import { useIntl } from "react-intl";

import ColliModal from "../colliModal";
import DimensionRow from "./dimensionRow";
import WrappingCheck from "./wrappingCheck";
import {
  setWeight,
  isLdmConsignment,
  validLoadingMeters,
  validCalculatedWeight,
} from "../../../modules/consignmentFunctions";
import ColliTableFooter from "./colliTableFooter";
import { orderByKey } from "../../../modules/linq";
import StateIcon from "../../app/generic/stateIcon";
import IconButton from "../../app/generic/iconButton";
import TableRowActions from "../../app/generic/tableRowActions";

import "./index.css";

function ColliForm({
  rules,
  detail,
  collis,
  product,
  adrCount,
  wrappings,
  onColliSave,
}) {
  const intl = useIntl();
  const isAdr = adrCount > 0;
  const [form, setForm] = useState();
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const [colliToUpdate, setColliToUpdate] = useState();
  const [wrappingModalVisible, setWrappingModalVisible] = useState();

  const handleCancel = useCallback(() => {
    setTimeout(() => setColliToUpdate(), 500);
    setVisible(false);
    form.resetFields();
  }, [form]);

  const handleShowClick = useCallback(() => setVisible(true), []);

  const saveFormRef = useCallback((formRef) => setForm(formRef), []);

  const handleCollisSave = useCallback(
    (collis) => {
      onColliSave(orderByKey(collis));
    },
    [onColliSave]
  );

  const handleOkClick = useCallback(() => {
    form.validateFields((err, values) => {
      setSaving(true);
      if (!err) {
        let key =
          colliToUpdate && values.count === 1
            ? colliToUpdate.key
            : Math.max(...collis.map((o) => o.key), -1) + 1;
        let weights = setWeight(values.weight, values.count, values.weightType),
          updatedColliId = colliToUpdate ? colliToUpdate.id : 0;
        handleCollisSave([
          ...(colliToUpdate
            ? collis.filter((c) => c.key !== colliToUpdate.key)
            : collis),
          ...[...Array(values.count)].map((_, i) => ({
            ...values,
            key: key++,
            count: 1,
            weight: weights[i],
            id: i === 0 ? updatedColliId : 0,
            barcode: i === 0 ? values.barcode : null,
          })),
        ]);
        handleCancel();
      }
      setSaving(false);
    });
  }, [form, collis, colliToUpdate, handleCancel, handleCollisSave]);

  const handleEdit = useCallback(
    (colli) => {
      setColliToUpdate(colli);
      handleShowClick();
      setTimeout(() => colli && form.validateFields(), 500);
    },
    [form, handleShowClick]
  );

  const handleDelete = useCallback(
    (colli) => {
      onColliSave(collis.filter((c) => c.key !== colli.key));
    },
    [onColliSave, collis]
  );

  const handleCopy = useCallback(
    (colli) => {
      handleCollisSave([
        ...collis,
        {
          ...colli,
          key: Math.max(...collis.map((o) => o.key), -1) + 1,
          count: 1,
          id: 0,
          barcode: null,
        },
      ]);
    },
    [collis, handleCollisSave]
  );

  const forceLoadingMeters = useMemo(
    () => isLdmConsignment(product, collis, adrCount),
    [collis, product, adrCount]
  );

  const renderFooter = useCallback(
    () => (
      <ColliTableFooter
        collis={collis}
        rules={rules}
        adrCount={adrCount}
        forceLoadingMeters={forceLoadingMeters}
      />
    ),
    [adrCount, collis, rules, forceLoadingMeters]
  );

  const columns = [
    {
      key: "wrappingId",
      width: 110,
      title: intl.formatMessage({
        id: "consignment.colli.wrapping",
        defaultMessage: "Druh obalu",
      }),
      dataIndex: "wrappingId",
      render: (wrappingId) => {
        const filterWrapping = wrappings.find((w) => w.id === wrappingId);
        return (
          <DimensionRow
            warning={filterWrapping}
            value={filterWrapping ? filterWrapping.name : "--"}
          />
        );
      },
    },
    {
      title: intl.formatMessage({
        id: "consignment.colli.dimensions",
        defaultMessage: "Rozměr kusu [cm]",
      }),
      children: [
        {
          key: "length",
          width: 125,
          title: intl.formatMessage({
            id: "consignment.colli.length",
            defaultMessage: "Délka",
          }),
          dataIndex: "length",
          render: (length, c) => (
            <DimensionRow
              value={length}
              errors={
                validLoadingMeters(c, rules, isAdr)
                  ? []
                  : [
                      intl.formatMessage(
                        {
                          id: "consignment.colli.overLoadingMeters",
                          defaultMessage:
                            "Překročena maximální přepočtená váha {max} kg!",
                        },
                        { max: rules.colliLoadingMeters }
                      ),
                    ]
              }
              warning={
                length <= rules.colliLength &&
                (!rules.colliMinLength || length >= rules.colliMinLength)
              }
            />
          ),
        },
        {
          key: "width",
          width: 125,
          title: intl.formatMessage({
            id: "consignment.colli.width",
            defaultMessage: "Šířka",
          }),
          dataIndex: "width",
          render: (width) => (
            <DimensionRow
              value={width}
              warning={
                width <= rules.colliWidth &&
                (!rules.colliMinWidth || width >= rules.colliMinWidth)
              }
            />
          ),
        },
        {
          key: "height",
          width: 125,
          title: intl.formatMessage({
            id: "consignment.colli.height",
            defaultMessage: "Výška",
          }),
          dataIndex: "height",
          render: (height) => (
            <DimensionRow
              value={height}
              warning={
                height <= rules.colliHeight &&
                (!rules.colliMinHeight || height >= rules.colliMinHeight)
              }
            />
          ),
        },
      ],
    },
    {
      key: "weight",
      width: 125,
      title: intl.formatMessage({
        id: "consignment.colli.weight",
        defaultMessage: "Hmotnost [kg]",
      }),
      dataIndex: "weight",
      render: (weight, c) => (
        <DimensionRow
          value={weight}
          errors={
            validCalculatedWeight(c, rules, isAdr)
              ? []
              : [
                  intl.formatMessage(
                    {
                      id: "consignment.colli.overCalculatedWeight",
                      defaultMessage:
                        "Překročena maximální přepočtená váha {max} kg!",
                    },
                    { max: rules.colliCalculatedWeight }
                  ),
                ]
          }
          warning={
            weight <= rules.colliWeight ||
            (rules.colliMinWeight && weight < rules.colliMinWeight)
          }
        />
      ),
    },
    {
      key: "stackable",
      width: 70,
      title:
        intl
          .formatMessage({
            id: "consignment.colli.stackable",
            defaultMessage: "Stohovatelné",
          })
          .substring(0, 4) + ".",
      dataIndex: "stackable",
      render: (stackable) => (
        <StateIcon
          close
          valid={stackable}
          messages={[
            stackable
              ? intl.formatMessage({
                  id: "consignment.colli.stackable.yes",
                  defaultMessage: "Kus je stohovatelný",
                })
              : intl.formatMessage({
                  id: "consignment.colli.stackable.no",
                  defaultMessage: "Kus není stohovatelný",
                }),
          ]}
        />
      ),
    },
    {
      key: "description",
      title: intl.formatMessage({
        id: "consignment.colli.description",
        defaultMessage: "Druh zboží",
      }),
      dataIndex: "description",
    },
    {
      key: "barcode",
      width: 150,
      title: intl.formatMessage({
        id: "consignment.colli.barcode",
        defaultMessage: "Číslo kusu",
      }),
      dataIndex: "barcode",
      render: (barcode) =>
        barcode ||
        intl.formatMessage({
          id: "consignment.colli.barcodeWillBeGenerated",
          defaultMessage: "bude automaticky vygenerováno po odeslání",
        }),
    },
    {
      key: "actions",
      width: 100,
      title: "",
      dataIndex: "",
      render: (c) => (
        <TableRowActions
          disabled={detail}
          onEdit={() => handleEdit(c)}
          onDelete={() => handleDelete(c)}
        >
          <IconButton
            icon="copy"
            disabled={detail}
            title={intl.formatMessage({
              id: "consignment.colli.copy",
              defaultMessage: "vytvořit kopii kusu",
            })}
            onClick={() => handleCopy(c)}
            className="table-row-action-button"
          />
        </TableRowActions>
      ),
    },
  ];

  return (
    <Row type="flex">
      <ColliModal
        rules={rules}
        saving={saving}
        visible={visible}
        ref={saveFormRef}
        wrappings={wrappings}
        colli={colliToUpdate}
        onCancel={handleCancel}
        onOkClick={handleOkClick}
        onWrappingWarnClick={setWrappingModalVisible}
      />
      <Col span={24}>
        <Table
          footer={renderFooter}
          locale={{
            emptyText: intl.formatMessage({
              id: "consignment.colli.noCollis",
              defaultMessage: "žádné zadané kusy",
            }),
          }}
          columns={columns}
          dataSource={collis}
          pagination={{ pageSize: 10 }}
          onRow={(record) => ({
            onDoubleClick: () => !detail && handleEdit(record),
          })}
        />
      </Col>
      <Col
        span={6}
        className={collis && collis.length > 0 ? "many-collis" : "no-collis"}
      >
        <Tooltip
          title={intl.formatMessage({
            id: "consignment.colli.adding",
            defaultMessage: "Přidat nový kus",
          })}
        >
          <Button
            icon="plus"
            type="primary"
            disabled={detail}
            onClick={handleShowClick}
          >
            {intl.formatMessage({
              id: "consignment.colli.adding",
              defaultMessage: "Přidat nový kus",
            })}
          </Button>
        </Tooltip>
      </Col>
      <WrappingCheck
        collis={collis}
        rules={rules}
        wrappings={wrappings}
        wrappingModalVisible={wrappingModalVisible}
        onSetWrappingModalVisible={setWrappingModalVisible}
      />
    </Row>
  );
}

export default ColliForm;
